import styled from 'styled-components';
import { DateCalendar } from '@mui/x-date-pickers';

interface StyledProps {
  isActive: boolean;
}

export const Wrapper = styled.aside`
  display: grid;
  justify-content: center;
  width: 252px;
  height: 100%;
  grid-template-rows: max-content max-content max-content;
  gap: 24px;
  background: ${({ theme }) => theme.color.grey50};
  padding: 20px 12px 32px;
  border-top: 1px solid ${({ theme }) => theme.color.grey300};
  border-right: 1px solid ${({ theme }) => theme.color.grey300};

  .MuiDateCalendar-root {
    width: 236px !important;
  }
`;

export const TimezoneStyled = styled.div`
  width: 100%;
`;

export const ButtonGroup = styled.div`
  display: flex;
  width: 281px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.color.grey300};
  box-shadow: 1px 1px 2px 0 rgba(54, 54, 54, 0.1);
`;

export const ButtonStyled = styled.button<StyledProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  padding: 10px;
  border: none;
  background: ${({ theme, isActive }) =>
    isActive ? theme.color.blue100 : theme.color.white};
  cursor: pointer;

  color: ${({ theme }) => theme.color.grey900};
  font-size: 14px;
  font-weight: ${({ isActive }) => (isActive ? '600' : '500')};
  line-height: 22.4px;

  &:hover {
    background: ${({ theme }) => theme.color.grey100};
  }

  &:first-child {
    min-width: fit-content;
    border-right: 1px solid ${({ theme }) => theme.color.grey300};
    border-radius: 8px 0 0 8px;
  }
  &:last-child {
    min-width: fit-content;
    flex: 1;
    border-radius: 0 8px 8px 0;
  }
`;

export const TimeViewSection = styled.section<StyledProps>`
  display: flex;
  flex-direction: column;

  > div:first-child {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 40px;
    padding: 8px 10px;
    border-radius: 4px;
    background: ${({ theme }) => theme.color.grey200};
    cursor: pointer;

    &:hover {
      background: ${({ theme }) => theme.color.grey300};
    }

    > h2 {
      color: ${({ theme }) => theme.color.grey700};
      font-size: 14px;
      font-weight: 500;
      line-height: 20px;
      letter-spacing: 0.1px;
    }

    > svg {
      transform: ${({ isActive }) => (isActive ? 'rotate(180deg)' : 'unset')};
      path {
        fill: ${({ theme }) => theme.color.grey700};
      }
    }
  }
`;

export const TimeViewList = styled.section`
  margin-top: 12px;
`;

export const RadioButtonStyled = styled.div<StyledProps>`
  margin-top: 8px;
  > div {
    input[type='radio'] {
      height: 16px;
      width: 16px;
    }
    > label {
      color: ${({ theme }) => theme.color.grey800};
      font-size: 14px;
      line-height: 19.6px;
      font-weight: ${({ isActive }) => (isActive ? '700' : '400')};
      > span {
        color: ${({ theme }) => theme.color.grey500};
        font-size: 14px;
        font-weight: 400;
        line-height: 19.6px;
        letter-spacing: 0.035px;
      }
    }
  }
  &:first-child {
    margin-top: 0;
  }
`;

export const ProfessionalSection = styled.section<StyledProps>`
  display: flex;
  flex-direction: column;

  > div:first-child {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 40px;
    padding: 8px 10px;
    border-radius: 4px;
    background: ${({ theme }) => theme.color.grey200};
    cursor: pointer;

    &:hover {
      background: ${({ theme }) => theme.color.grey300};
    }

    > h2 {
      color: ${({ theme }) => theme.color.grey700};
      font-size: 14px;
      font-weight: 500;
      line-height: 20px;
      letter-spacing: 0.1px;
    }

    > svg {
      transform: ${({ isActive }) => (isActive ? 'rotate(180deg)' : 'unset')};
      path {
        fill: ${({ theme }) => theme.color.grey700};
      }
    }
  }

  > div:nth-child(2) {
    margin-top: 12px;
    input[type='search'] {
      font-size: 14px;
      ::placeholder {
        font-size: 14px;
      }
    }
  }
`;

export const CheckboxSection = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 272px;
  overflow-y: auto;
  margin-top: 4px;
`;

export const CheckboxStyled = styled.div<StyledProps>`
  > div {
    input + label:before {
      width: 16px;
      height: 16px;
    }
    input + label:after {
      width: 18px;
      height: 18px;
      top: -1px;
    }
    &:first-child {
      margin-top: 8px;
    }
    > label {
      color: ${({ theme }) => theme.color.grey800};
      font-family: Mulish;
      font-size: 14px;
      font-style: normal;
      font-weight: ${({ isActive }) => (isActive ? '700' : '400')};
      letter-spacing: 0.07px;
    }
  }
`;

export const DateCalendarStyled = styled(DateCalendar)`
  .MuiDateCalendar-root {
    width: 236px !important;
  }
  .MuiPickersCalendarHeader-root.MuiPickersCalendarHeader-root {
    padding: 0;
    margin-top: 0;
    .MuiPickersCalendarHeader-label {
      margin-right: 6px;
      color: ${({ theme }) => theme.color.grey900};
      text-align: center;
      font-size: 14px;
      font-weight: 500;
      line-height: 20px;
      letter-spacing: 0.1px;
    }
    .MuiPickersArrowSwitcher-root {
      .MuiPickersArrowSwitcher-spacer {
        width: 15px;
      }
      > button {
        padding: 0;
        margin: 0;
        height: fit-content;
        svg {
          path {
            fill: ${({ theme }) => theme.color.grey700};
          }
        }
      }
    }
  }

  .MuiPickersFadeTransitionGroup-root {
    .Mui-selected {
      font-size: 12px;
    }
    .MuiDayCalendar-slideTransition {
      min-height: unset;
      > * {
        position: unset;
      }
    }
    .MuiDayCalendar-header.MuiDayCalendar-header {
      width: 100%;
      justify-content: space-between;
      > span {
        width: 26px;
        height: 26px;
        color: ${({ theme }) => theme.color.grey900};
        text-align: center;
        font-size: 12px;
        font-weight: 500;
        line-height: 16.8px;
        letter-spacing: 0.5px;
      }
    }

    .MuiDayCalendar-weekContainer {
      width: 100%;
      margin: 0;
      align-items: center;
      justify-content: space-between;
      .MuiPickersDay-dayWithMargin.MuiPickersDay-dayOutsideMonth {
        color: ${({ theme }) => theme.color.grey300};
        text-align: center;
        font-size: 12px;
        font-weight: 400;
        line-height: 16.8px;
        letter-spacing: 0.5px;
      }
      .MuiPickersDay-today {
        background-color: ${({ theme }) => theme.color.blue300};
        color: ${({ theme }) => theme.color.grey900};
        text-align: center;
        font-size: 12px;
        font-weight: 400;
        line-height: 16.8px;
        letter-spacing: 0.5px;
      }

      > button {
        width: 26px;
        height: 26px;
        padding: 0;
        margin: 0;
      }
    }
  }
`;

export const DropdownStyled = styled.div`
  width: 236px;

  h2 {
    font-size: 14px;
    line-height: 19.6px;
  }

  .optionList {
    div {
      font-size: 14px;
      line-height: 19.6px;
    }
  }
`;
